import type { Maybe, ValueEvent } from '@adornis/base/utilTypes.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { XNativeDialog } from '@adornis/dialog/x-native-dialog.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { StringAspect } from '@adornis/filter/aspect-schemas/StringAspect.js';
import { FormController } from '@adornis/forms/x-form-controller.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { CapacityAPI } from '@chancenwerk/shared/capacity/api/CapacityAPI.js';
import {
  CapacityRequest,
  CapacityRequestStatus,
  CapacityRequestType,
} from '@chancenwerk/shared/capacity/entities/CapacityRequest.js';
import { ChancenwerkUserFilter } from '@chancenwerk/shared/filter/ChancenwerkUserFilter.js';
import { Weekday } from '@chancenwerk/enums/enums.js';
import '@chancenwerk/client/design/components/cr-date-picker-dropdown.js';
import { ChromiumDialog } from '@chancenwerk/client/design/components/cr-dialog.js';
import '@chancenwerk/client/design/components/cr-input.js';
import { KPFAPI } from '@chancenwerk/shared/master-data-management/kpf/api/KPFAPI.js';
import { KPFAspect } from '@chancenwerk/shared/master-data-management/kpf/entities/aspects/KPFAspect.js';
import { KPF } from '@chancenwerk/shared/master-data-management/kpf/entities/KPF.js';
import { UserAPI } from '@chancenwerk/shared/master-data-management/users/api/UserAPI.js';
import { ChancenwerkUser } from '@chancenwerk/shared/master-data-management/users/entities/ChancenwerkUser.js';
import { ExternerMitarbeiter } from '@chancenwerk/shared/master-data-management/users/entities/ExternerMitarbeiter.js';
import { Mitarbeiter } from '@chancenwerk/shared/master-data-management/users/entities/Mitarbeiter.js';
import { VerwaltungsMitarbeiter } from '@chancenwerk/shared/master-data-management/users/entities/VerwaltungsMitarbeiter.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { DateTime } from 'luxon';
import '../kraefte-component-workshop-attendance.js';

@customElement('kraefte-dialog-edit-capacity')
export class KraefteDialogEditCapacity extends ChromiumDialog<string> {
  @property({ attribute: false }) public request = new RXController(this, new CapacityRequest({}));
  @property({ attribute: false }) public status = new RXController(this, null as Maybe<CapacityRequestStatus>);
  @property({ type: Boolean }) public hideOnboarding = false;

  @state() private _weekdaysChanged = false;
  @state() private _currentUser = new RXController(this, currentUser());
  @state() private _form = new FormController(this, this.request.observable, {
    onSuccess: async doc => {
      if (doc._id) {
        await doc.save();
        this.close(doc._id);
        await CapacityAPI.notifyChange(doc._id)();
      } else {
        const id = await doc.create();
        this.close(id);
        await CapacityAPI.notifyNew(id)();
      }
    },
  });

  @state() private _hideEntityPicker = false;

  static override get element_name() {
    return 'kraefte-dialog-edit-capacity';
  }

  override heading = 'Bedarf anmelden';

  private async _save() {
    if (!this._form.document) {
      return;
    }

    if (this.request.value._id && this._weekdaysChanged) {
      if (
        !(await XNativeDialog.confirm(
          'Die Wochentage wurden geändert. Bitte bachte das du eventuell neue Bedarfe anmelden musst! Fortfahren?',
        ))
      ) {
        return;
      }
    }

    if (this._form.document._id) {
      this._form.document.editedByID = this._currentUser?.value?._id ?? '';
      this._form.document.editedAt = DateTime.now();
    } else {
      this._form.document.createdByID = this._currentUser?.value?._id ?? '';
      this._form.document.createdAt = DateTime.now();
    }

    if (this.status.value) {
      this._form.document.status = this.status.value;
    }

    await this._form.validate();
  }

  private _renderBaseData() {
    return html`
      <cr-flex space="sm">
        <chancen-component-card-title>Basisdaten</chancen-component-card-title>
        <x-entity-picker
          placeholder="KPF"
          clearable
          .options=${{
            class: KPF,
            operation: KPFAPI.subscribeByFilter,
            selectionSet: { name: 1, kuerzel: 1, produkt: { _class: 1, kuerzel: 1 }, school: { name: 1, kuerzel: 1 } },
            filter: new AdornisFilter({}),
          }}
          ${this._form.field('KPFID')}
        ></x-entity-picker>

        <cr-dropdown-selection
          placeholder="LeKo / SchuKo"
          .selectables=${Object.values(CapacityRequestType)}
          ${this._form.field('type')}
        ></cr-dropdown-selection>

        <cr-date-picker-dropdown
          .end-year=${DateTime.now().plus({ years: 5 }).year}
          placeholder="Ab wann?"
          click-to-save
          clearable
          ${this._form.field('fromWhen')}
        ></cr-date-picker-dropdown>

        <cr-dropdown-multiselection
          .selectables=${Object.values(Weekday)}
          placeholder="Wochentage"
          ${this._form.field('weekdays')}
          @value-picked=${() => {
            this._weekdaysChanged = true;
          }}
        ></cr-dropdown-multiselection>

        ${this.status.value
          ? nothing
          : html`<cr-dropdown-selection
              .selectables=${Object.values(CapacityRequestStatus)}
              placeholder="Status"
              ${this._form.field('status')}
            ></cr-dropdown-selection>`}

        <cr-input placeholder="besondere Fähigkeiten" ${this._form.field('specialSkills')}></cr-input>
        <cr-checkbox label="hohe Priorität" ${this._form.field('highPriority')}></cr-checkbox>

        ${this._comment()}
      </cr-flex>
    `;
  }

  private _comment() {
    return html`
      <br />
      <chancen-component-card-title>Kommentar</chancen-component-card-title>
      <cr-input placeholder="Kommentar" ${this._form.field('comment')}></cr-input>
    `;
  }

  private _renderOnboardingData() {
    if (this.hideOnboarding) {
      return nothing;
    }

    return html`
      <cr-flex space="sm">
        <chancen-component-card-title>Einstellungsdaten</chancen-component-card-title>
        <cr-input
          placeholder="Vorname"
          ${this._form.field('vorname')}
          @value-picked=${() => {
            this._hideEntityPicker = true;
            setTimeout(() => (this._hideEntityPicker = false), 100);
            this.requestUpdate();
          }}
        ></cr-input>
        <cr-input
          placeholder="Nachname"
          ${this._form.field('nachname')}
          @value-picked=${() => {
            this._hideEntityPicker = true;
            setTimeout(() => (this._hideEntityPicker = false), 100);
            this.requestUpdate();
          }}
        ></cr-input>
        <cr-input placeholder="Telefon Bewerber" ${this._form.field('telefonBewerber')}></cr-input>
        <cr-input placeholder="Bewerbungslink" ${this._form.field('bewerbungslink')}></cr-input>
        <cr-date-picker-dropdown
          placeholder="Schnuppertag Datum"
          click-to-save
          clearable
          ${this._form.field('schnuppertagDatum')}
        ></cr-date-picker-dropdown>

        <cr-date-picker-dropdown
          placeholder="Willkommensmail gesendet"
          click-to-save
          clearable
          ${this._form.field('datumVersandWillkommensmail')}
        ></cr-date-picker-dropdown>

        <cr-date-picker-dropdown
          placeholder="erste Teilnahme Lernförderung"
          click-to-save
          clearable
          ${this._form.field('datumErsteTeilnahmeLernfoerderung')}
        ></cr-date-picker-dropdown>

        <cr-date-picker-dropdown
          placeholder="Feedbackunterlagen erhalten"
          click-to-save
          clearable
          ${this._form.field('datumFeedbackunterlagenErhalten')}
        ></cr-date-picker-dropdown>

        <cr-date-picker-dropdown
          placeholder="Feedbackgespräch"
          click-to-save
          clearable
          ${this._form.field('datumFeedbackgespraech')}
        ></cr-date-picker-dropdown>
      </cr-flex>

      ${this._workshopLinking()}
    `;
  }

  private _workshopLinking() {
    return html`
      <cr-flex space="sm">
        <chancen-component-card-title> Workshops </chancen-component-card-title>

        <cr-text ${css({ opacity: 0.7, marginBottom: this.spacing.md })}>
          Falls der Bewerber im CHANCENraum existiert, kannst du ihn hier auswählen, um seine Workshopteilnahmen zu
          sehen.
        </cr-text>

        ${this._hideEntityPicker
          ? nothing
          : html` <x-entity-picker
              ${css({ maxWidth: '720px' })}
              placeholder="verlinkte Person CHANCENraum"
              clearable
              .value=${this._form.document?.linkedUserID}
              .options=${{
                class: ChancenwerkUser,
                selectionSet: { vorname: 1, nachname: 1 },
                operation: UserAPI.subscribeByFilter,
                filter: new ChancenwerkUserFilter({
                  userClass: [Mitarbeiter._class, VerwaltungsMitarbeiter._class, ExternerMitarbeiter._class],
                  aspects: [
                    new KPFAspect({ value: this._form.document?.KPFID ?? '' }),
                    ...(this._form.document?.vorname
                      ? [new StringAspect({ fieldName: 'vorname', mode: 'starts', value: this._form.document.vorname })]
                      : []),
                    ...(this._form.document?.nachname
                      ? [
                          new StringAspect({
                            fieldName: 'nachname',
                            mode: 'starts',
                            value: this._form.document.nachname,
                          }),
                        ]
                      : []),
                  ],
                }),
              }}
              @value-picked=${(e: ValueEvent<Maybe<string>>) => {
                if (!this._form.document) return;
                this._form.document.linkedUserID = e.detail.value;
                this.requestUpdate();
              }}
            ></x-entity-picker>`}
        ${this._form.document?.linkedUserID
          ? html`<kraefte-component-workshop-attendance
              .userID=${this._form.document.linkedUserID}
            ></kraefte-component-workshop-attendance>`
          : nothing}
      </cr-flex>
    `;
  }

  override content() {
    return html`
      <cr-grid
        columns=${this.hideOnboarding ? '1fr' : '1fr 1fr 0.7fr'}
        space="md"
        ${css({ width: this.hideOnboarding ? '400px' : '1000px' })}
      >
        ${this._renderBaseData()} ${this._renderOnboardingData()}
      </cr-grid>
      <cr-flex horizontal ${css({ justifyContent: 'end' })} space="md">
        <cr-button icon="edit" @click=${() => this._save()} ${css({ maxWidth: '200px' })}>
          ${this._form.document?._id ? 'Speichern' : 'Hinzufügen'}
        </cr-button>
        ${this.request.value._id
          ? html`
              <cr-button
                tone="error"
                icon="delete"
                @click=${async () => {
                  await ChromiumDialog.guard(
                    async () => {
                      await CapacityAPI.notifyDelete(this.request.value.KPFID ?? '', this.request.value.type ?? '')();
                      await this.request.value.remove();
                      this.close('');
                    },
                    { hasToConfirm: true, confirmText: 'Bewerbung wirklich löschen?' },
                  );
                }}
                ${css({ maxWidth: '200px' })}
              >
                Löschen
              </cr-button>
            `
          : nothing}
      </cr-flex>
    `;
  }
}
